/**
 * Studio Admin → Runs. Paste a run, session or project id (or a MicroVM or
 * lease id) from a log or Slack and go straight to its page, and see which runs
 * failed in the last day across every organization.
 */
import { Loader2, RotateCw, Search } from 'lucide-react';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { type AdminFailedRunsResponse, type AdminLookupResult, adminClient } from '@/api/adminClient';
import { LookupResults } from '@/components/search/LookupResults';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { LoadError, Skeleton, SkeletonList } from '@/components/ui/load-state';
import { useAdminLookup } from '@/hooks/useAdminLookup';
import { exactLookupMatch, moveHighlight, orderedLookupResults } from '@/lib/adminSearch';
import { formatCount, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

const LIST_ID = 'runs-lookup-results';

function RunLookup() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const lookup = useAdminLookup(query, 150);
  const ordered = useMemo(() => orderedLookupResults(lookup.results), [lookup.results]);
  const settled = !lookup.loading && lookup.query === query.trim() && query.trim().length >= 2;

  useEffect(() => {
    setHighlighted(ordered.length > 0 ? 0 : -1);
  }, [ordered]);

  useEffect(() => {
    if (!settled) return;
    const exact = exactLookupMatch(lookup.query, lookup.results);
    if (exact) navigate(exact.path);
  }, [settled, lookup.query, lookup.results, navigate]);

  const open = (result: AdminLookupResult | undefined) => {
    if (result) navigate(result.path);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => moveHighlight(current, event.key === 'ArrowDown' ? 1 : -1, ordered.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      open(ordered[highlighted]);
    }
  };

  return (
    <AdminSection title="Find a run">
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Paste a run, session or project id"
            aria-label="Paste a run, session or project id"
            aria-controls={LIST_ID}
            autoComplete="off"
            spellCheck={false}
            className="h-14 w-full rounded-xl border border-input bg-background pl-12 pr-12 font-mono text-base placeholder:font-sans placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {lookup.loading ? (
            <Loader2 className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" aria-label="Looking it up" />
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          An exact id opens its page straight away. MicroVM and lease ids work too, and open the session they served.
        </p>
        {lookup.error ? <p className="text-sm text-destructive-foreground">The lookup didn't work: {lookup.error}</p> : null}
        {ordered.length > 0 ? (
          <LookupResults results={ordered} highlighted={highlighted} onSelect={open} onHighlight={setHighlighted} listId={LIST_ID} />
        ) : settled && !lookup.error ? (
          <p className="text-sm text-muted-foreground">Nothing has the id “{query.trim()}”. Check it was copied whole.</p>
        ) : null}
      </div>
    </AdminSection>
  );
}

function FailedRuns() {
  const [data, setData] = useState<AdminFailedRunsResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(undefined);
    try {
      setData(await adminClient.listFailedRuns());
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load failed runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = (
    <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
      <RotateCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} aria-hidden="true" />
      Refresh
    </Button>
  );

  return (
    <AdminSection title="Failed in the last 24 hours" actions={refresh} bodyClassName="p-0">
      {!data && loading ? (
        <SkeletonList
          rows={4}
          label="Loading failed runs"
          className="divide-y divide-border"
          rowClassName="flex items-center justify-between gap-4 px-5 py-3.5"
          renderRow={() => (
            <>
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-24" />
            </>
          )}
        />
      ) : !data ? (
        <LoadError message={loadError} onRetry={() => void load()} />
      ) : data.items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground">No runs have failed in the last 24 hours.</p>
      ) : (
        <div className="px-5 pb-4 pt-2">
          <DataTable>
            <DataTableHead columns={[{ label: 'When' }, { label: 'Organization' }, { label: 'Project' }, { label: 'What they asked' }, { label: 'What went wrong' }]} />
            <tbody>
              {data.items.map((run) => (
                <DataTableRow key={run.runId}>
                  <DataTableCell className="whitespace-nowrap">
                    <Link to={run.path} className="font-medium text-foreground underline-offset-2 hover:underline">
                      {formatDateTime(run.createdAt)}
                    </Link>
                  </DataTableCell>
                  <DataTableCell>{run.organizationName}</DataTableCell>
                  <DataTableCell className="text-muted-foreground">{run.projectName ?? run.projectId}</DataTableCell>
                  <DataTableCell className="max-w-[18rem]">
                    <Link to={run.path} className="line-clamp-2 hover:underline">
                      {run.promptSnippet ?? 'No prompt recorded'}
                    </Link>
                  </DataTableCell>
                  <DataTableCell className="max-w-[22rem] text-muted-foreground">
                    <span className="line-clamp-2">{run.error ?? 'No error message recorded'}</span>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </tbody>
          </DataTable>
          {data.truncated ? (
            <p className="mt-2 text-xs text-muted-foreground">Showing the newest {formatCount(data.items.length)}. Older failures are on each organization's pages.</p>
          ) : null}
        </div>
      )}
    </AdminSection>
  );
}

export function RunsPage() {
  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader title="Runs" description="Open any run from an id in a log or Slack, and see what failed recently across every organization." />
      <div className="flex flex-col gap-6">
        <RunLookup />
        <FailedRuns />
      </div>
    </div>
  );
}
